import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { CheckCircle, User, ArrowLeft, AlertCircle } from "lucide-react";
import { DatePicker } from "./DatePicker";

// Configuração dinâmica de URL para compatibilidade com links compartilhados
const getBackendUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env?.REACT_APP_BACKEND_URL;
  if (envUrl) {
    return envUrl;
  }
  
  if (typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.host}`;
  }
  
  return '';
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Lista de funções predefinidas (mesma do backend)
const FUNCOES_PRESTADOR = [
  "CAMAREIRO(A)",
  "ASG",
  "AUXILIAR DE LAVANDERIA",
  "SUPERVISOR DE ANDARES",
  "RECEPCIONISTA",
  "MENSAGEIRO",
  "GARÇOM",
  "STEWARD",
  "COZINHEIRO",
  "BARTENDER",
  "HOSTESS",
  "CAIXA",
  "CONFEITEIRO"
];

const CadastroPrestadorPublico = () => {
  const navigate = useNavigate();
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    cpf_cnpj: '',
    data_nascimento: '',
    rg: '',
    rg_data_expedicao: '',
    pis: '',
    cnpj_mei: '',
    email: '',
    telefone: '',
    cep: '',
    endereco: '',
    numero: '',
    bairro: '',
    cidade: '',
    estado: '',
    filiacao_pai: '',
    filiacao_mae: '',
    funcoes: [],
    chave_pix: '',
    forma_pagamento: '',
    observacoes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [camposComErro, setCamposComErro] = useState({});
  const [tentouSalvar, setTentouSalvar] = useState(false);

  // Função para buscar endereço pelo CEP via ViaCEP
  const buscarCep = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    
    setBuscandoCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error("CEP não encontrado");
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        endereco: data.logradouro || prev.endereco,
        bairro: data.bairro || prev.bairro,
        cidade: data.localidade || prev.cidade,
        estado: data.uf || prev.estado
      }));
      
      if (data.logradouro) {
        toast.success("Endereço preenchido automaticamente!");
      }
    } catch (error) {
      toast.error("Erro ao buscar CEP. Preencha manualmente.");
    } finally {
      setBuscandoCep(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTentouSalvar(true);

    // Validação de campos obrigatórios (mesma regra do backend)
    const erros = {};
    if (!formData.nome || !formData.nome.trim()) erros.nome = 'Este campo é obrigatório';
    if (!formData.cpf_cnpj || !formData.cpf_cnpj.trim()) erros.cpf_cnpj = 'Este campo é obrigatório';
    if (!formData.data_nascimento) erros.data_nascimento = 'Este campo é obrigatório';
    if (!formData.filiacao_pai || !formData.filiacao_pai.trim()) erros.filiacao_pai = 'Este campo é obrigatório';
    if (!formData.filiacao_mae || !formData.filiacao_mae.trim()) erros.filiacao_mae = 'Este campo é obrigatório';
    if (!formData.funcoes || formData.funcoes.length === 0) erros.funcoes = 'Selecione ao menos uma função';
    if (!formData.cep || !formData.cep.trim()) erros.cep = 'Este campo é obrigatório';
    if (!formData.endereco || !formData.endereco.trim()) erros.endereco = 'Este campo é obrigatório';
    if (!formData.numero || !formData.numero.trim()) erros.numero = 'Este campo é obrigatório';
    if (!formData.bairro || !formData.bairro.trim()) erros.bairro = 'Este campo é obrigatório';
    if (!formData.cidade || !formData.cidade.trim()) erros.cidade = 'Este campo é obrigatório';
    if (!formData.estado || !formData.estado.trim()) erros.estado = 'Este campo é obrigatório';
    if (!formData.email || !formData.email.trim()) {
      erros.email = 'Este campo é obrigatório';
    } else if (!formData.email.includes('@') || !formData.email.includes('.')) {
      erros.email = 'Formato de e-mail inválido';
    }
    if (!formData.telefone || !formData.telefone.trim()) {
      erros.telefone = 'Este campo é obrigatório';
    } else if (formData.telefone.replace(/\D/g, '').length < 10) {
      erros.telefone = 'Mínimo 10 dígitos';
    }
    if (!formData.chave_pix || !formData.chave_pix.trim()) erros.chave_pix = 'Este campo é obrigatório';
    if (!formData.forma_pagamento || !formData.forma_pagamento.trim()) erros.forma_pagamento = 'Este campo é obrigatório';

    setCamposComErro(erros);
    if (Object.keys(erros).length > 0) {
      toast.error("Existem campos obrigatórios não preenchidos. Verifique os campos destacados em vermelho.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Preparar dados para envio
      const dataToSend = {
        ...formData,
        data_nascimento: formData.data_nascimento || null,
        rg_data_expedicao: formData.rg_data_expedicao || null
      };
      
      await axios.post(`${API}/prestadores/auto-cadastro`, dataToSend);
      
      setIsSuccess(true);
      setTentouSalvar(false);
      setCamposComErro({});
      toast.success("Cadastro realizado com sucesso!");
      
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || "CPF já cadastrado no sistema");
      } else {
        toast.error("Erro ao realizar cadastro. Tente novamente.");
      }
      console.error('Erro no cadastro:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVoltar = () => {
    navigate('/');
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-600">Cadastro Realizado!</CardTitle>
            <CardDescription className="text-base">
              Seu cadastro foi enviado com sucesso e está aguardando aprovação do administrador.
              <br /><br />
              <strong>Importante:</strong> Este cadastro é apenas para fins operacionais (escala de trabalho e lançamento de diárias). 
              Você não terá acesso ao sistema - caso precise de informações, entre em contato com a administração.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              onClick={() => {
                setIsSuccess(false);
                setFormData({
                  nome: '', cpf_cnpj: '', data_nascimento: '', rg: '', rg_data_expedicao: '',
                  pis: '', cnpj_mei: '', email: '', telefone: '', cep: '', endereco: '',
                  numero: '', bairro: '', cidade: '', estado: '', filiacao_pai: '',
                  filiacao_mae: '', funcoes: [], chave_pix: '', forma_pagamento: '', observacoes: ''
                });
              }} 
              className="w-full"
              variant="outline"
            >
              Fazer Novo Cadastro
            </Button>
            <Button 
              onClick={handleVoltar} 
              className="w-full"
            >
              Voltar para o Login
            </Button>
          </CardContent>
        </Card>
        <Toaster />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="mb-4">
            <img 
              src="/logo.png" 
              alt="Logo da Empresa" 
              className="h-16 w-auto mx-auto object-contain"
              style={{ maxWidth: '200px' }}
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Cadastro de Prestador</h1>
          <p className="text-gray-600 mt-2">Preencha seus dados para se cadastrar como prestador de serviços</p>
        </div>

        {/* Aviso importante */}
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800 font-medium">Informação importante</p>
            <p className="text-sm text-amber-700">
              Este cadastro é exclusivo para <strong>Prestadores de Serviço</strong>. 
              Você será cadastrado para fins operacionais (escala e diárias), mas <strong>não terá acesso ao sistema</strong>. 
              O acesso ao sistema é restrito à equipe administrativa.
            </p>
          </div>
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <User className="h-5 w-5" />
                <CardTitle>Dados Pessoais</CardTitle>
              </div>
              <Button variant="ghost" size="sm" onClick={handleVoltar}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            </div>
            <CardDescription>
              Preencha os campos obrigatórios. Seu cadastro será analisado pela administração.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6 [&_input]:uppercase [&_textarea]:uppercase">
              {/* Dados Básicos */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Identificação</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="nome">Nome Completo</Label>
                    <Input
                      id="nome"
                      value={formData.nome}
                      onChange={(e) => setFormData({...formData, nome: e.target.value})}
                      className={tentouSalvar && camposComErro.nome ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.nome && (
                      <p className="text-red-500 text-xs mt-1">{camposComErro.nome}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="cpf_cnpj">CPF</Label>
                    <Input
                      id="cpf_cnpj"
                      value={formData.cpf_cnpj}
                      onChange={(e) => setFormData({...formData, cpf_cnpj: e.target.value})}
                      placeholder="000.000.000-00"
                      className={tentouSalvar && camposComErro.cpf_cnpj ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.cpf_cnpj && (
                      <p className="text-red-500 text-xs mt-1">{camposComErro.cpf_cnpj}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="data_nascimento">Data de Nascimento</Label>
                    <DatePicker
                      id="data_nascimento"
                      value={formData.data_nascimento}
                      onChange={(value) => setFormData({...formData, data_nascimento: value})}
                      placeholder="Selecione a data"
                      className={tentouSalvar && camposComErro.data_nascimento ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.data_nascimento && (
                      <p className="text-red-500 text-xs mt-1">{camposComErro.data_nascimento}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Documentos */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Documentos</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="rg">RG</Label>
                    <Input
                      id="rg"
                      value={formData.rg}
                      onChange={(e) => setFormData({...formData, rg: e.target.value})}
                      placeholder="00.000.000-0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="rg_data_expedicao">Data Expedição RG</Label>
                    <DatePicker
                      id="rg_data_expedicao"
                      value={formData.rg_data_expedicao}
                      onChange={(value) => setFormData({...formData, rg_data_expedicao: value})}
                      placeholder="Selecione a data"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pis">PIS</Label>
                    <Input
                      id="pis"
                      value={formData.pis}
                      onChange={(e) => setFormData({...formData, pis: e.target.value})}
                      placeholder="000.00000.00-0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cnpj_mei">CNPJ (MEI)</Label>
                    <Input
                      id="cnpj_mei"
                      value={formData.cnpj_mei}
                      onChange={(e) => setFormData({...formData, cnpj_mei: e.target.value})}
                      placeholder="00.000.000/0001-00"
                    />
                  </div>
                </div>
              </div>

              {/* Filiação */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Filiação</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="filiacao_pai">Nome do Pai</Label>
                    <Input
                      id="filiacao_pai"
                      value={formData.filiacao_pai}
                      onChange={(e) => setFormData({...formData, filiacao_pai: e.target.value})}
                      className={tentouSalvar && camposComErro.filiacao_pai ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.filiacao_pai && (
                      <p className="text-red-500 text-xs mt-1">{camposComErro.filiacao_pai}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="filiacao_mae">Nome da Mãe</Label>
                    <Input
                      id="filiacao_mae"
                      value={formData.filiacao_mae}
                      onChange={(e) => setFormData({...formData, filiacao_mae: e.target.value})}
                      className={tentouSalvar && camposComErro.filiacao_mae ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.filiacao_mae && (
                      <p className="text-red-500 text-xs mt-1">{camposComErro.filiacao_mae}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Funções */}
              <div className="space-y-4">
                <h3 className={`text-lg font-medium border-b pb-2 ${tentouSalvar && camposComErro.funcoes ? 'text-red-600' : 'text-gray-900'}`}>Funções que você exerce</h3>
                <p className="text-sm text-gray-600">Selecione todas as funções que você pode exercer:</p>
                {tentouSalvar && camposComErro.funcoes && (
                  <p className="text-red-500 text-xs">{camposComErro.funcoes}</p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {FUNCOES_PRESTADOR.map((funcao) => (
                    <div key={funcao} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`funcao-${funcao}`}
                        checked={formData.funcoes.includes(funcao)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({...formData, funcoes: [...formData.funcoes, funcao]});
                          } else {
                            setFormData({...formData, funcoes: formData.funcoes.filter(f => f !== funcao)});
                          }
                        }}
                        className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                      />
                      <Label htmlFor={`funcao-${funcao}`} className="text-sm cursor-pointer">
                        {funcao}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Endereço */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Endereço</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="cep">CEP</Label>
                    <div className="flex gap-2">
                      <Input
                        id="cep"
                        value={formData.cep}
                        onChange={(e) => {
                          const cep = e.target.value;
                          setFormData({...formData, cep});
                          if (cep.replace(/\D/g, '').length === 8) {
                            buscarCep(cep);
                          }
                        }}
                        placeholder="00000-000"
                        className={`flex-1 ${tentouSalvar && camposComErro.cep ? 'border-red-500' : ''}`}
                      />
                      {buscandoCep && (
                        <span className="flex items-center text-sm text-gray-500">...</span>
                      )}
                    </div>
                    {tentouSalvar && camposComErro.cep && (
                      <p className="text-red-500 text-xs mt-1">{camposComErro.cep}</p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="endereco">Logradouro</Label>
                    <Input
                      id="endereco"
                      value={formData.endereco}
                      onChange={(e) => setFormData({...formData, endereco: e.target.value})}
                      placeholder="Rua, Avenida..."
                      className={tentouSalvar && camposComErro.endereco ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.endereco && (
                      <p className="text-red-500 text-xs mt-1">{camposComErro.endereco}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="numero">Número</Label>
                    <Input
                      id="numero"
                      value={formData.numero}
                      onChange={(e) => setFormData({...formData, numero: e.target.value})}
                      placeholder="123"
                      className={tentouSalvar && camposComErro.numero ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.numero && (
                      <p className="text-red-500 text-xs mt-1">{camposComErro.numero}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="bairro">Bairro</Label>
                    <Input
                      id="bairro"
                      value={formData.bairro}
                      onChange={(e) => setFormData({...formData, bairro: e.target.value})}
                      className={tentouSalvar && camposComErro.bairro ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.bairro && (
                      <p className="text-red-500 text-xs mt-1">{camposComErro.bairro}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="cidade">Cidade</Label>
                    <Input
                      id="cidade"
                      value={formData.cidade}
                      onChange={(e) => setFormData({...formData, cidade: e.target.value})}
                      className={tentouSalvar && camposComErro.cidade ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.cidade && (
                      <p className="text-red-500 text-xs mt-1">{camposComErro.cidade}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="estado">Estado</Label>
                    <Input
                      id="estado"
                      value={formData.estado}
                      onChange={(e) => setFormData({...formData, estado: e.target.value})}
                      placeholder="SP"
                      maxLength={2}
                      className={tentouSalvar && camposComErro.estado ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.estado && (
                      <p className="text-red-500 text-xs mt-1">{camposComErro.estado}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Contato */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Contato</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className={`!normal-case ${tentouSalvar && camposComErro.email ? 'border-red-500' : ''}`}
                    />
                    {tentouSalvar && camposComErro.email && (
                      <p className="text-red-500 text-xs mt-1">{camposComErro.email}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="telefone">Telefone</Label>
                    <Input
                      id="telefone"
                      value={formData.telefone}
                      onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                      placeholder="(11) 99999-9999"
                      className={tentouSalvar && camposComErro.telefone ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.telefone && (
                      <p className="text-red-500 text-xs mt-1">{camposComErro.telefone}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Pagamento */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Dados para Pagamento</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="chave_pix">Chave PIX</Label>
                    <Input
                      id="chave_pix"
                      value={formData.chave_pix}
                      onChange={(e) => setFormData({...formData, chave_pix: e.target.value})}
                      placeholder="CPF, email, telefone ou chave aleatória"
                      className={`!normal-case ${tentouSalvar && camposComErro.chave_pix ? 'border-red-500' : ''}`}
                    />
                    {tentouSalvar && camposComErro.chave_pix && (
                      <p className="text-red-500 text-xs mt-1">{camposComErro.chave_pix}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="forma_pagamento">Forma de Pagamento Preferida</Label>
                    <Input
                      id="forma_pagamento"
                      value={formData.forma_pagamento}
                      onChange={(e) => setFormData({...formData, forma_pagamento: e.target.value})}
                      placeholder="PIX, Transferência, etc."
                      className={tentouSalvar && camposComErro.forma_pagamento ? 'border-red-500' : ''}
                    />
                    {tentouSalvar && camposComErro.forma_pagamento && (
                      <p className="text-red-500 text-xs mt-1">{camposComErro.forma_pagamento}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Observações */}
              <div>
                <Label htmlFor="observacoes">Observações</Label>
                <Textarea
                  id="observacoes"
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  placeholder="Informações adicionais, experiência profissional, disponibilidade, etc."
                  rows={3}
                />
              </div>

              {/* Submit */}
              <div className="pt-6 space-y-3">
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full"
                  size="lg"
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Cadastro'}
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  Seu cadastro será analisado pela administração. Este cadastro não dá acesso ao sistema.
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
      <Toaster />
    </div>
  );
};

export default CadastroPrestadorPublico;
